/**
 * Read-only window describing how the (muted) VS video is slaved onto the raw
 * WAV time axis.
 *
 * Design (PHASE38, WAV-master): the WAV is the single source of truth for
 * audio, waveform, and tags — they all share the raw WAV/CSV time axis with no
 * warping. The video is shorter and offset; it is layered on top by mapping a
 * raw WAV time to a video time via `waveToVideo` and is only present within
 * `[videoCoversStartSec, videoCoversEndSec]`.
 *
 * There is intentionally NO time-stretch/scale: video and WAV are the same
 * real-time recording at rate 1, so the mapping is a pure head offset.
 */
export interface AvSyncWindow {
  /** True for a VS record with a known video duration → slave video to wave. */
  readonly enabled: boolean;

  /** Head offset: `videoTime = waveTime - videoOffsetSec`. */
  readonly videoOffsetSec: number;

  /** Video clip duration (seconds). */
  readonly videoDurationSec: number;

  /** Raw WAV time where the video begins (= videoOffsetSec). */
  readonly videoCoversStartSec: number;

  /** Raw WAV time where the video ends (= videoOffsetSec + videoDurationSec). */
  readonly videoCoversEndSec: number;

  /**
   * Map a raw WAV time to the corresponding video time, clamped to
   * [0, videoDurationSec]. Identity when disabled.
   */
  waveToVideo(waveTime: number): number;

  /** True when the given raw WAV time falls inside the video coverage range. */
  videoCovers(waveTime: number): boolean;
}

/**
 * Explicit per-record VS video range on the raw WAV/CSV time axis.
 * `waveEndSec` is optional because old CSVs may only include a start marker.
 */
export interface AvSyncVideoRange {
  readonly waveStartSec: number;
  readonly waveEndSec?: number | null;
}

/** Disabled identity window (used when not VS or video duration unknown). */
export const IDENTITY_AV_SYNC_WINDOW: AvSyncWindow = {
  enabled: false,
  videoOffsetSec: 0,
  videoDurationSec: 0,
  videoCoversStartSec: 0,
  videoCoversEndSec: Number.POSITIVE_INFINITY,
  waveToVideo: (waveTime: number) => waveTime,
  videoCovers: () => true,
};
